import React, { forwardRef, useContext, useEffect, useImperativeHandle, useRef, useState } from 'react';
import { ApiContext } from '@api/api';
import useTranslationLgs from '@hooks/i18n/useTranslation';
import AuditLogTable, { AuditLogTableHandle, AuditSharedFilter, IT_OWNER_ACTOR, IT_OWNER_NAME } from '@components/RingilComponents/audit/AuditLogTable.tsx';
import { AuditEntityTypeEnum, AuditFilter, EntityTypeEnum, Pageable, PageResponseAuditEntryOutDTO } from '@api/ringil-api.v2';

const NONE = undefined as unknown as string;

interface ComponentProps {
  entityType: AuditEntityTypeEnum;
  entityId: string;
  /** Suppress the own headline when a surrounding SectionPanel already provides one. */
  hideHeader?: boolean;
}

export interface EntityAuditLogHandle {
  reload: () => void;
}

// Entity-scoped audit log: fixes entityType+entityId, tenant endpoints. Thin wrapper over AuditLogTable.
const EntityAuditLog = forwardRef<EntityAuditLogHandle, ComponentProps>(({ entityType, entityId, hideHeader = false }, ref) => {
  const { queryAuditLog, auditActors, auditActions } = useContext(ApiContext);
  const { tr } = useTranslationLgs();
  const tableRef = useRef<AuditLogTableHandle>(null);
  const [actorOptions, setActorOptions] = useState<{ label: string; value: string }[]>([]);
  const [actionOptions, setActionOptions] = useState<{ label: string; value: string }[]>([]);

  const loadLovs = () => {
    // role (ACTOR | ON_BEHALF_OF) left unset — the filter offers every actor.
    // The IT OWNER LoV entry maps to the impersonated=true filter, not its actorId (BE contract).
    auditActors(entityType, entityId, NONE, data =>
      setActorOptions((data ?? []).map(a => ({ label: a.name, value: a.name === IT_OWNER_NAME ? IT_OWNER_ACTOR : a.id }))),
    );
    // Facet: only the actions actually present for this entity, labelled by the BE-supplied key.
    // Actions are BE-owned; category/outcome are FE keys built inside AuditLogTable (no LoV needed).
    auditActions(entityType, entityId, data =>
      setActionOptions((data ?? []).map(a => ({ label: tr(a.activityKey, a.auditAction), value: a.auditAction }))),
    );
  };

  // reload() refreshes the table and the LoVs together, so a newly-acting actor/action shows up in the filters.
  useImperativeHandle(ref, () => ({
    reload: () => {
      tableRef.current?.reload();
      loadLovs();
    },
  }));

  useEffect(() => {
    loadLovs();
  }, [entityType, entityId]);

  const fetchPage = (pageable: Pageable, shared: AuditSharedFilter, _tableFilter: any, onSuccess: (resp: PageResponseAuditEntryOutDTO) => void) => {
    // AuditFilter.entityType still types to the legacy EntityTypeEnum (no PARTNER), so cast.
    const filter: AuditFilter = { ...shared, entityType: entityType as unknown as EntityTypeEnum, entityId };
    queryAuditLog(pageable, filter, onSuccess);
  };

  return <AuditLogTable ref={tableRef} fetchPage={fetchPage} actorOptions={actorOptions} actionOptions={actionOptions} filterName={`audit_log_${entityType}`} hideHeader={hideHeader} />;
});

EntityAuditLog.displayName = 'EntityAuditLog';

export default EntityAuditLog;
